const objectHash = require('./objectHash');
const create = require('./create');

function getCacheKey (args) {
  let cacheKey = objectHash(args || []);
  return cacheKey;
}

/*
options: {
  ttl: number,
  selfRefresh: {
    interval: number
  },
}
*/

function memoize(fn, options) {
  // if options is a number, it's the ttl
  if (typeof options === 'number') {
    options = { ttl: options };
  }

  let ttl = options.ttl;
  if (! ttl || isNaN(ttl)) {
    throw new Error('ttl is required');
  }

  const shouldSelfRefresh = !!options.selfRefresh;
  let selfRefreshInterval = options?.selfRefresh?.interval;
  if (selfRefreshInterval) {
    selfRefreshInterval = Math.floor(selfRefreshInterval * 1000);
  } else {
    selfRefreshInterval = Math.floor(ttl/2 * 1000);
  }

  const cache = create();

  const waitingPromises = {};

  const selfRefreshTimeouts = {};

  // returns promise
  const memoizer = function (...args) {
    let cacheKey = getCacheKey(args);
    const cachedResult = cache.get(cacheKey);

    // If we haven't scheduled a self-refresh for this cache key, and we should, let's schedule one
    if (shouldSelfRefresh && ! selfRefreshTimeouts[cacheKey]) {
      let msToRefresh = selfRefreshInterval;

      if (cachedResult !== undefined) {
        const remainingTtlMs = cache.getRemainingTtlMs(cacheKey);
        if (remainingTtlMs) {
          // If we have a cached result, subtract the time passed since the result was cached from the self-refresh interval
          const passedMs = Math.floor(ttl * 1000) - remainingTtlMs;
          msToRefresh -= passedMs;
        }
      }

      if (msToRefresh > 0) {
        selfRefreshTimeouts[cacheKey] = setTimeout(() => {
          memoizer.refresh(...args);
        }, msToRefresh);
      }
    }

    return new Promise((resolve, reject) => {
      // Result is already cached, let's get it!
      if (cachedResult !== undefined) {
        resolve(cachedResult);
        return;
      }

      // A promise is already waiting for this result, let's wait for it!
      if (cacheKey in waitingPromises) {
        waitingPromises[cacheKey].push({resolve,reject});
        return;
      }

      // No result is cached, and no other function is currently waiting for the same result.
      waitingPromises[cacheKey] = [{resolve,reject}];

      fn(...args).then(result => {
        cache.set(cacheKey, result, ttl);

        // Resolve all promises waiting for this result
        for (const p of waitingPromises[cacheKey]) {
          p.resolve(result);
        }
        delete waitingPromises[cacheKey];
      }).catch(err => {
        // Reject all promises waiting for this result
        for (const p of waitingPromises[cacheKey]) {
          p.reject(err);
        }
        delete waitingPromises[cacheKey];
      });
    });
  };

  memoizer.hydrate = function (args, value) {
    cache.set(getCacheKey(args), value, ttl);
  }

  memoizer.refresh = async function (...args) {
    let cacheKey = getCacheKey(args);
    const result = await fn(...args);
    cache.set(cacheKey, result, ttl);

    // Clear the self-refresh timeout so that new calls will trigger a new timeout
    delete selfRefreshTimeouts[cacheKey];

    return result;
  };

  return memoizer;
}

module.exports = memoize;